<script setup lang="ts">
import { getPropertyByKey } from '@/core/helpers/objectHelper';
import { useStore } from '@/core/hooks/useStore';
import { AxiosRequestConfig } from 'axios';
import { computed, onMounted, PropType, ref, toRefs, watch } from 'vue';

const { dispatch } = useStore();
const { locale } = useI18n();

const props: any = defineProps({
   name: { type: String, default: null, required: true },
   label: { type: String, default: '' },
   disabled: { type: Boolean, default: false },
   required: { type: Boolean, default: false },
   placeholder: { type: String, default: null },
   searchPlaceholder: { type: String, default: null },
   monospace: { type: Boolean, default: false },

   modelValue: {
      type: [String, Number, Array, Boolean, Object] as PropType<string | number | boolean | object | Array<any> | null>,
      default: null,
   },
   uniqueKey: { type: String, default: 'id' },
   displayKey: { type: String, default: 'name' },
   options: { type: Array, default: () => [] },
   searchType: {
      type: String,
      default: 'internal',
      validator: (value: string) => ['none', 'internal', 'custom'].includes(value),
   },
   searchMethod: { type: Function as PropType<(query: AxiosRequestConfig) => Promise<any>>, default: null },
   queryParams: { type: Object, default: () => ({}) },
   multiselect: { type: Boolean, default: false },
   searchOnProperties: { type: Array as PropType<Array<string>>, default: () => [] },

   errors: { type: Object, default: () => ({}) },
   description: { type: String, default: '' },
   url: { type: String, default: null },
   hasClearOption: { type: Boolean, default: true },
   initialDataCallback: { type: Function, default: null },

   optionsUrl: { type: String, default: null },
   emitObject: { type: Boolean, default: false },

   ignoreOptions: { type: Array as PropType<Array<number | string>>, default: () => [] },

   loadMoreType: { type: String as PropType<'scroll' | 'button'>, default: 'button' },
   loadMoreMeta: { type: Object, default: () => null },

   groupDisplayKey: { type: String, default: '' },
   groupUniqueKey: { type: String, default: '' },
   grouped: { type: Boolean, default: false },
   dropdownClass: { type: String, default: '' },
   showApplyButton: { type: Boolean, default: false },
});

const emit = defineEmits(['update:model-value']);

const filteredOptions = ref([] as Array<any>);
const currentPage = ref(1);
const hasMore = ref(false);
const isLoadingMore = ref(false);

const { options, ignoreOptions, queryParams, modelValue, initialDataCallback, displayKey } = toRefs(props);

watch(
   options,
   (newValue, oldValue) => {
      // Reset pagination when options change
      currentPage.value = 1;
      hasMore.value = false;

      if (filteredOptions.value?.length <= 0) filteredOptions.value = newValue || [];
      if ((oldValue?.length || 0) > 0 && (newValue?.length || 0) <= 0) filteredOptions.value = [];
      else if (newValue?.length > 0) filteredOptions.value = [...newValue];
   },
   { immediate: true },
);

onMounted(() => {
   if (props.optionsUrl && props.searchType == 'internal') {
      dispatch(props.optionsUrl).then((response) => {
         if (Array.isArray(response?.data)) return (filteredOptions.value = response.data);
         if (Array.isArray(response)) return (filteredOptions.value = response);

         return (filteredOptions.value = []);
      });
   }
});

const searchValue = ref();

function normalizeResponseData(response: any): Array<any> {
   if (Array.isArray(response?.data?.value?.data)) return response.data.value.data;
   if (Array.isArray(response?.data)) return response.data;
   if (Array.isArray(response)) return response;
   return [];
}

function handleSearch({ query, done }: { query: string; done: () => void }) {
   // Reset pagination when new search starts
   currentPage.value = 1;
   hasMore.value = false;

   let params = { ...queryParams.value };

   searchValue.value = query;

   if (query) {
      params = { ...queryParams.value, [`search`]: query, per_page: 25, page: 1 };
   }

   if (props.searchMethod) {
      props
         .searchMethod({ params })
         .then((response: any) => {
            const data = normalizeResponseData(response);
            const filteredData = data.filter((o: any) => !ignoreOptions.value.find((io: any) => io == o[props.uniqueKey]));

            if (props.loadMoreType === 'scroll' || props.loadMoreType === 'button') {
               const meta = response?.meta ?? props.loadMoreMeta;
               hasMore.value = meta?.total > meta?.per_page * currentPage.value;
            }

            filteredOptions.value = filteredData;
         })
         .finally(done);
   } else if (props.url) {
      dispatch(props.url, params)
         .then((response) => {
            const data = normalizeResponseData(response);
            const filteredData = data.filter((o: any) => !ignoreOptions.value.find((io: any) => io == o[props.uniqueKey]));

            if (props.loadMoreType === 'scroll' || props.loadMoreType === 'button') {
               const meta = response?.meta ?? props.loadMoreMeta;
               hasMore.value = meta?.total > meta?.per_page * currentPage.value;
            }

            filteredOptions.value = filteredData;
         })
         .finally(done);
   } else {
      console.warn(`The search type was set to custom, but no url was provided.`);
      done();
   }
}

function loadMore() {
   if (isLoadingMore.value || !hasMore.value) return;

   isLoadingMore.value = true;
   currentPage.value += 1;

   let params = { ...queryParams.value, page: currentPage.value, per_page: 25, search: searchValue.value };
   const executeRequest = props.searchMethod ? props.searchMethod({ params }) : dispatch(props.url, params);

   executeRequest
      .then((response: any) => {
         const data = normalizeResponseData(response);
         const filteredData = data.filter((o: any) => !ignoreOptions.value.find((io: any) => io == o[props.uniqueKey]));

         const meta = response?.meta ?? props.loadMoreMeta;
         hasMore.value = meta?.total > meta?.per_page * currentPage.value;
         filteredOptions.value = [...filteredOptions.value, ...filteredData];
      })
      .finally(() => {
         isLoadingMore.value = false;
      });
}

watch(modelValue, fetchInitialData, { immediate: true });

async function fetchInitialData(newValue?: any) {
   const hasValue = filteredOptions.value?.find((o) => getPropertyByKey(props.uniqueKey, o) == newValue);
   if (!initialDataCallback.value || !newValue || hasValue) return;

   const option = await initialDataCallback.value();
   if (!option) return;

   filteredOptions.value = [option];
}

const saveDisplayKey = computed(() => {
   if ((filteredOptions.value || []).length <= 0) return displayKey.value;

   const intendedKey = getPropertyByKey(displayKey.value, filteredOptions.value[0]);
   if (typeof intendedKey === 'string') {
      return displayKey.value;
   }

   const translatedKey = getPropertyByKey(`${displayKey.value}.${locale.value}`, filteredOptions.value[0]);
   if (typeof translatedKey === 'string') return `${displayKey.value}.${locale.value}`;

   if (typeof filteredOptions.value[0]?.['name'] === 'object') return 'name.nl';

   return 'name';
});
</script>

<template>
   <NxtSelectBridge
      v-bind="props"
      :class="'dropdown'"
      :options="filteredOptions"
      :search-placeholder="searchPlaceholder || $t('actions.search', 2)"
      :placeholder="placeholder || $t('generic.form.select')"
      :display-key="saveDisplayKey"
      :search-on-properties="searchOnProperties"
      :has-more="hasMore"
      :has-more-type="loadMoreType"
      :monospace
      :is-loading-more="isLoadingMore"
      :has-clear-option="hasClearOption"
      :clear-search-on-select="true"
      :group-display-key="groupDisplayKey"
      :group-unique-key="groupUniqueKey"
      :dropdown-class
      :grouped="grouped"
      @on-search="handleSearch"
      @load-more-options="loadMore()"
      @update:model-value="(value: any) => emit('update:model-value', value)"
   >
      <template v-if="$slots.label" #label="params">
         <slot name="label" v-bind="params"></slot>
      </template>
      <template #icon v-if="$slots.icon">
         <slot name="icon"> </slot>
      </template>
      <template #clear>
         <slot name="clear">
            <span>{{ $t('actions.clear') }}</span>
         </slot>
      </template>
      <template #seachPlaceholder>
         <slot name="callToAction">
            <span>{{ $t('generic.feedback.start-typing-to-search') }}</span>
         </slot>
      </template>
      <template #noOptions>
         <slot name="noResults">
            <span>{{ $t('generic.feedback.no-results', 2) }}</span>
         </slot>
      </template>
      <template #loading>
         <slot name="loading">
            <span>{{ $t('generic.feedback.loading') }}...</span>
         </slot>
      </template>
      <template v-if="$slots.before" #before>
         <slot name="before"></slot>
      </template>
      <template v-if="$slots.after" #after>
         <slot name="after">
            <span>{{ $t('generic.feedback.no-results', 2) }}</span>
         </slot>
      </template>
      <template #displayOption="{ option }: { option: any }">
         <slot name="displayOption" v-bind="{ option }"></slot>
      </template>
      <template #option="{ option, index }: { option: any; index: number }">
         <slot name="option" v-bind="{ option, index }"> </slot>
      </template>
      <template #badge="{ option, removeOption }">
         <slot name="badge" v-bind="{ option, removeOption }"></slot>
      </template>
      <template #multiDisplayOptions="{ modelValue }">
         <slot name="multiDisplayOptions" v-bind="{ modelValue }"></slot>
      </template>
   </NxtSelectBridge>
</template>

<style lang="scss">
.dropdown {
   .option {
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
   }
   .nxt-dropdown__list {
      transform: translateX(0);
      z-index: var(--zindex-fixed);
   }
   .nxt-dropdown__main {
      transform: translateX(0);
   }
   .nxt-dropdown__list-item {
      min-height: var(--nxt-input-height);
      height: auto;

      &[data-unique-key='__load_more__'] {
         font-style: italic;
         color: var(--nxt-color-primary);
         border-top: 1px solid var(--nxt-color-border);
         cursor: pointer;

         &:hover {
            background-color: var(--nxt-color-primary-light);
         }
      }
   }
}
</style>
